//src/components/gro/QuestionnaireVersionPreview.tsx
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import type {
  QuestionnaireQuestion,
  QuestionnaireSection,
  QuestionnaireVersionDetail,
} from "@/types/gro";

const RESPONSE_TYPE_LABEL: Record<string, string> = {
  single_choice: "Escala de avaliação (Likert)",
  multiple_choice: "Múltipla escolha",
  numeric: "Numérica inteira",
  text: "Texto livre",
  system_select: "Seleção do sistema",
  boolean: "Booleana",
};

const SYSTEM_SELECT_SOURCE_LABEL: Record<string, string> = {
  departments: "Departamentos",
  job_positions: "Cargos",
  units: "Unidades",
};

function sortQuestions(questions: QuestionnaireQuestion[]) {
  return [...questions].sort((a, b) => a.question_order - b.question_order);
}

function sortSections(sections: QuestionnaireSection[]) {
  return [...sections].sort(
    (a, b) =>
      a.section_order - b.section_order ||
      a.title.localeCompare(b.title, "pt-BR", { sensitivity: "base" }),
  );
}

export function QuestionnaireVersionPreview({
  version,
}: {
  version: QuestionnaireVersionDetail;
}) {
  const sections = sortSections(version.sections);
  const unsectionedQuestions = sortQuestions(
    version.questions.filter((q) => !q.section_id),
  );

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm text-muted-foreground">Pré-visualização</div>
            <div className="text-lg font-semibold">
              {version.version_label?.trim()
                ? version.version_label
                : `v${version.version_number}`}
            </div>
          </div>

          <Badge variant="outline">
            {version.status === "draft" ? "Rascunho" : "Publicado"}
          </Badge>
        </div>

        {version.instructions ? (
          <div className="rounded-lg border bg-muted/20 p-4 text-sm leading-relaxed">
            {version.instructions}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Este questionário não possui instruções iniciais.
          </div>
        )}
      </Card>

      {unsectionedQuestions.length > 0 ? (
        <Card className="p-6">
          <div className="mb-4">
            <div className="text-base font-semibold">Perguntas sem seção</div>
            <div className="text-sm text-muted-foreground">
              Itens exibidos antes das seções do questionário.
            </div>
          </div>

          <div className="space-y-4">
            {unsectionedQuestions.map((question) => (
              <QuestionPreviewCard key={question.id} question={question} />
            ))}
          </div>
        </Card>
      ) : null}

      {sections.map((section) => {
        const questions = sortQuestions(
          version.questions.filter((q) => q.section_id === section.id),
        );

        return (
          <Card key={section.id} className="p-6">
            <div className="mb-4">
              <div className="text-sm text-muted-foreground">
                Seção {section.section_order}
              </div>
              <div className="text-lg font-semibold">{section.title}</div>
              {section.description ? (
                <div className="mt-1 text-sm text-muted-foreground">
                  {section.description}
                </div>
              ) : null}
            </div>

            {questions.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                Nenhuma pergunta cadastrada nesta seção.
              </div>
            ) : (
              <div className="space-y-4">
                {questions.map((question) => (
                  <QuestionPreviewCard key={question.id} question={question} />
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function FakeRadio() {
  return (
    <span className="flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground/40 bg-background">
      <span className="h-2 w-2 rounded-full bg-transparent" />
    </span>
  );
}

function QuestionPreviewCard({
  question,
}: {
  question: QuestionnaireQuestion;
}) {
  const responseLabel =
    RESPONSE_TYPE_LABEL[question.response_type] ?? question.response_type;

  return (
    <div className="rounded-xl border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge variant="outline">#{question.question_order}</Badge>
        <Badge variant="secondary">{responseLabel}</Badge>
        {question.is_required ? <Badge variant="outline">Obrigatória</Badge> : null}
      </div>

      <div className="font-medium leading-relaxed">{question.statement}</div>

      {question.help_text ? (
        <div className="mt-2 text-sm text-muted-foreground">
          {question.help_text}
        </div>
      ) : null}

      <div className="mt-4">
        {question.response_type === "text" ? (
          <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
            Campo de resposta aberta
          </div>
        ) : question.response_type === "numeric" ? (
          <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
            Campo numérico inteiro
          </div>
        ) : question.response_type === "system_select" ? (
          <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
            Seleção única de {SYSTEM_SELECT_SOURCE_LABEL[question.system_select_source || ""] || "cadastro do sistema"}
          </div>
        ) : question.options.length === 0 ? (
          <div className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
            Nenhuma opção cadastrada
          </div>
        ) : (
          <div className="space-y-2">
            {[...question.options]
              .sort((a, b) => a.option_order - b.option_order)
              .map((option) => (
                <label
                  key={option.id}
                  className="flex items-center gap-3 rounded-lg border px-3 py-2 text-sm"
                >
                  {question.response_type === "multiple_choice" ? (
                    <Checkbox checked={false} disabled />
                  ) : (
                    <FakeRadio />
                  )}
                  <span>{option.option_label}</span>
                </label>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
